import type { TurnError } from "../contract"
import { emptyTurn, type TurnStatus, type TurnView } from "../store"

/** One stored turn from `GET /threads/{id}`: `{turn_id, question, status, outcome}`. */
export interface StoredTurn {
  turnId: string
  question: string
  status: string
  /** Documents the turn was asked with, when the server stored them. */
  docIds: string[]
  view: TurnView | null
}

const ACTIVE = new Set(["running", "pending", "active", "queued"])

type Obj = Record<string, unknown>
const obj = (v: unknown): Obj | null => (v && typeof v === "object" && !Array.isArray(v) ? (v as Obj) : null)
const str = (v: unknown, d = ""): string => (typeof v === "string" ? v : d)

function outcomeStatus(s: string): TurnStatus {
  if (s === "complete" || s === "done") return "done"
  if (s === "awaiting_input") return "awaiting_input"
  if (s === "partial" || s === "failed" || s === "error") return "error"
  return "done"
}

function outcomeError(o: Obj, status: TurnStatus): TurnError | undefined {
  const e = obj(o.error)
  if (e) {
    return {
      code: (str(e.code, "internal") as TurnError["code"]),
      message: str(e.message, "NIRA could not finish this answer."),
      retryable: e.retryable !== false,
      partial: e.partial === true,
    }
  }
  if (status !== "error") return undefined
  return {
    code: "internal",
    message: "NIRA could not finish this answer.",
    retryable: true,
    partial: str(o.status) === "partial",
  }
}

/** Stored plan has the live `sections.plan` shape `{sections: [...]}`; a bare array is accepted too. */
function planOf(v: unknown): TurnView["plan"] {
  if (Array.isArray(v)) return v as TurnView["plan"]
  const s = obj(v)?.sections
  return Array.isArray(s) ? (s as TurnView["plan"]) : undefined
}

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [])

/** Build a turn view from a stored outcome (the final payload the server kept). */
export function viewFromOutcome(turnId: string, question: string, outcome: Obj): TurnView {
  const status = outcomeStatus(str(outcome.status))
  const base = emptyTurn(turnId)
  const sections = obj(outcome.sections) ?? {}
  return {
    ...base,
    status,
    question,
    mode: str(outcome.mode, "ask"),
    clarify: (obj(outcome.clarify) ?? undefined) as unknown as TurnView["clarify"],
    head: (obj(outcome.head) ?? undefined) as unknown as TurnView["head"],
    plan: planOf(outcome.plan),
    sections: sections as TurnView["sections"],
    sources: (obj(outcome.sources) ?? {}) as TurnView["sources"],
    followups: Array.isArray(outcome.followups) ? (outcome.followups as string[]) : [],
    limits: strings(outcome.limits),
    found: Array.isArray(outcome.found) ? (outcome.found as TurnView["found"]) : [],
    trail: (obj(outcome.search_trail) ?? obj(outcome.trail) ?? undefined) as TurnView["trail"],
    error: outcomeError(outcome, status),
    mascot: "idle",
  }
}

/** Parse the thread payload defensively; unknown shapes yield no turns. */
/** The thread's case file, `{id, title}`, when it is in one. */
export function caseOf(data: unknown): { id: string; title: string } | null {
  const c = obj(obj(data)?.case)
  return c && str(c.id) ? { id: str(c.id), title: str(c.title) } : null
}

export function parseThread(data: unknown): { title: string; turns: StoredTurn[] } {
  const d = obj(data)
  if (!d) return { title: "", turns: [] }
  const rows = Array.isArray(d.turns) ? d.turns : []
  const turns: StoredTurn[] = []
  for (const r of rows) {
    const row = obj(r)
    if (!row) continue
    const turnId = str(row.turn_id) || str(row.id)
    if (!turnId) continue
    const question = str(row.question)
    const outcome = obj(row.outcome)
    const docIds = strings(row.doc_ids).length ? strings(row.doc_ids) : strings(obj(row.request)?.doc_ids)
    turns.push({ turnId, question, status: str(row.status), docIds, view: outcome ? viewFromOutcome(turnId, question, outcome) : null })
  }
  return { title: str(d.title), turns }
}

/** A stored turn that has no outcome yet and may still be running on the server. */
export const isActive = (t: StoredTurn) => !t.view && (ACTIVE.has(t.status) || t.status === "")

/** Stored turns replaced by a later run of the same question (an edit, a retry, a re-run), oldest first. */
export function supersededIds(turns: StoredTurn[]): string[] {
  const out: string[] = []
  for (let i = 0; i < turns.length - 1; i++) {
    const q = turns[i].question.trim()
    if (q && q === turns[i + 1].question.trim()) out.push(turns[i].turnId)
  }
  return out
}
